import { useState, type ReactNode } from 'react';
import {
  CANCEL_CODE_NEEDING_NOTE,
  INTERVIEW_CANCEL_REASONS,
  PANELIST_ROLE_LABELS,
  RESCHEDULE_INITIATOR_LABELS,
  RESCHEDULE_INITIATORS,
  type InterviewCancelReasonCode,
  type InterviewView,
  type RescheduleInitiator,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Checkbox, Radio, RadioGroup } from '../../../components/Checkbox';
import { DatePicker } from '../../../components/DatePicker';
import { Field } from '../../../components/Field';
import { Textarea } from '../../../components/Input';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { TimePicker } from '../../../components/TimePicker';
import { dhakaToIso } from '../../../lib/dhaka-time';
import { usePanelOptions, useTimeCheck } from '../api/interviews-api';
import type { useInterviewActions } from '../hooks/use-interview-actions';
import styles from './Interviews.module.css';

// S19 — the small panels SCH-04 opens beside the control that asked for them
// (CLAUDE.md §5: pop-ups anchor to their trigger). SCH-05 is the first two.

type Actions = ReturnType<typeof useInterviewActions>;

/** The clashes the server named, and the button that accepts them (the double-booking warning). */
export function WarningList({ warnings }: { readonly warnings: readonly string[] }): ReactNode {
  if (warnings.length === 0) return null;
  return (
    <ul className={styles.warningList} role="alert">
      {warnings.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}

/** A date and a start time, typed in Dhaka time (as S18's exam sessions are). */
export function DhakaTimeFields({
  date,
  time,
  onDate,
  onTime,
  min,
}: {
  readonly date: string;
  readonly time: string;
  readonly onDate: (value: string) => void;
  readonly onTime: (value: string) => void;
  readonly min?: string;
}): ReactNode {
  return (
    <div className={styles.fieldRow}>
      <Field label="Date" hint="Dhaka time">
        {({ inputId, describedBy }) => <DatePicker id={inputId} label="Interview date" value={date} onChange={onDate} {...(min ? { min } : {})} {...(describedBy ? { describedBy } : {})} />}
      </Field>
      <Field label="Start">
        {({ inputId }) => <TimePicker id={inputId} label="Start time" value={time} onChange={onTime} />}
      </Field>
    </div>
  );
}

/**
 * SCH-05 / F10.07 — who asked, and optionally the new time at once. Without one the time
 * is released and the scheduler opens to send times or book (§L25 rescheduled).
 */
export function ReschedulePanel({
  interview,
  actions,
  today,
  onMoved,
  onCancel,
}: {
  readonly interview: InterviewView;
  readonly actions: Actions;
  readonly today: string;
  readonly onMoved: (hasNewTime: boolean) => void;
  readonly onCancel: () => void;
}): ReactNode {
  const [initiator, setInitiator] = useState<RescheduleInitiator>('candidate');
  const [panelistUserId, setPanelistUserId] = useState<string | null>(interview.panel[0]?.userId ?? null);
  const [note, setNote] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [refusal, setRefusal] = useState<{ readonly startsAt: string | null; readonly warnings: readonly string[] } | null>(null);
  const [isPending, setIsPending] = useState(false);
  const startsAt = date && time ? dhakaToIso(date, time) : null;
  // The server's clash refusal holds only for the time it answered: a new time clears it, so "Move anyway" can never
  // accept another time's clashes (D92).
  const warnings = refusal?.startsAt === startsAt ? refusal.warnings : [];
  const check = useTimeCheck(interview.id, startsAt, null);
  const clashes = check.data?.startsAt === startsAt ? (check.data?.warnings ?? []) : [];

  const submit = async (isConflictAccepted: boolean): Promise<void> => {
    setIsPending(true);
    const result = await actions.reschedule(interview, {
      initiator,
      panelistUserId: initiator === 'panelist' ? panelistUserId : null,
      note: note.trim() === '' ? null : note.trim(),
      startsAt,
      isConflictAccepted,
    });
    setIsPending(false);
    if (Array.isArray(result)) setRefusal({ startsAt, warnings: result });
    else if (result) onMoved(startsAt !== null);
  };

  return (
    <div className={styles.panel}>
      <p className={styles.panelTitle}>Move {interview.candidateName}'s interview</p>
      <RadioGroup>
        {RESCHEDULE_INITIATORS.map((value) => (
          <Radio key={value} name="initiator" value={value} checked={initiator === value} label={RESCHEDULE_INITIATOR_LABELS[value]} onChange={() => setInitiator(value)} />
        ))}
      </RadioGroup>
      {initiator === 'panelist' && (
        <Field label="Which panelist">
          {({ inputId }) => (
            <Select
              id={inputId}
              label="Which panelist"
              value={panelistUserId}
              options={interview.panel.map((member) => ({ value: member.userId, label: member.fullName }))}
              onChange={setPanelistUserId}
            />
          )}
        </Field>
      )}
      {initiator === 'candidate' && <p className={styles.panelBody}>The deadline clock pauses until the new time is set.</p>}
      <Field label="Note" isOptional>
        {({ inputId }) => <Textarea id={inputId} rows={2} value={note} placeholder="Has an exam that morning." onChange={(event) => setNote(event.target.value)} />}
      </Field>
      <DhakaTimeFields date={date} time={time} onDate={setDate} onTime={setTime} min={today} />
      <p className={styles.panelBody}>{startsAt ? 'Booked at the new time in one step; everyone is told.' : 'Leave the time empty to release it, then send new times or book one.'}</p>
      <WarningList warnings={warnings.length > 0 ? warnings : clashes} />
      <div className={styles.panelActions}>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Keep it
        </Button>
        {warnings.length > 0 ? (
          <Button variant="solid" size="sm" isPending={isPending} onClick={() => void submit(true)}>
            Move anyway
          </Button>
        ) : (
          <Button variant="solid" size="sm" isPending={isPending} disabled={(date !== '') !== (time !== '')} onClick={() => void submit(clashes.length > 0)}>
            {startsAt ? (clashes.length > 0 ? 'Move anyway' : 'Move to this time') : 'Release the time'}
          </Button>
        )}
      </div>
    </div>
  );
}

/** SCH-05 / F10.08 — this interview only; the plan never changes. */
export function SwapPanel({ interview, actions, onDone, onCancel }: { readonly interview: InterviewView; readonly actions: Actions; readonly onDone: () => void; readonly onCancel: () => void }): ReactNode {
  const options = usePanelOptions(interview.id, true);
  const [fromUserId, setFromUserId] = useState<string | null>(interview.panel[0]?.userId ?? null);
  const [toUserId, setToUserId] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<readonly string[]>([]);
  const [isPending, setIsPending] = useState(false);

  const submit = async (isConflictAccepted: boolean): Promise<void> => {
    if (!fromUserId || !toUserId) return;
    setIsPending(true);
    const result = await actions.substitute(interview, fromUserId, toUserId, isConflictAccepted);
    setIsPending(false);
    if (Array.isArray(result)) setWarnings(result);
    else if (result) onDone();
  };

  return (
    <div className={styles.panel}>
      <p className={styles.panelTitle}>Swap a panelist</p>
      <Field label="Who is out">
        {({ inputId }) => (
          <Select
            id={inputId}
            label="Who is out"
            value={fromUserId}
            options={interview.panel.map((member) => ({ value: member.userId, label: member.fullName, description: PANELIST_ROLE_LABELS[member.role] }))}
            onChange={(value) => {
              setFromUserId(value);
              setWarnings([]);
            }}
          />
        )}
      </Field>
      <Field label="Who stands in">
        {({ inputId }) =>
          options.isPending ? (
            <SkeletonLines count={1} />
          ) : (
            <Select
              id={inputId}
              label="Who stands in"
              value={toUserId}
              placeholder="Choose someone"
              options={(options.data ?? []).map((person) => ({ value: person.id, label: person.fullName, description: person.email }))}
              onChange={(value) => {
                setToUserId(value);
                setWarnings([]);
              }}
            />
          )
        }
      </Field>
      <p className={styles.panelBody}>For this interview only — the job's interview plan stays as it is.</p>
      <WarningList warnings={warnings} />
      <div className={styles.panelActions}>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" size="sm" isPending={isPending} disabled={!fromUserId || !toUserId} onClick={() => void submit(warnings.length > 0)}>
          {warnings.length > 0 ? 'Swap anyway' : 'Swap'}
        </Button>
      </div>
    </div>
  );
}

/** F10.09 — a reason, always (non-negotiable #2); "Other" needs a note (§L19). */
export function CancelPanel({ interview, actions, onDone, onCancel }: { readonly interview: InterviewView; readonly actions: Actions; readonly onDone: () => void; readonly onCancel: () => void }): ReactNode {
  const [code, setCode] = useState<InterviewCancelReasonCode | null>(null);
  const [note, setNote] = useState('');
  const [isPending, setIsPending] = useState(false);
  const needsNote = code === CANCEL_CODE_NEEDING_NOTE && note.trim() === '';
  const wasTold = interview.startsAt !== null;

  return (
    <div className={styles.panel}>
      <p className={styles.panelTitle}>Cancel this interview?</p>
      <RadioGroup>
        {INTERVIEW_CANCEL_REASONS.map((reason) => (
          <Radio key={reason.code} name="cancel-reason" value={reason.code} checked={code === reason.code} label={reason.label} onChange={() => setCode(reason.code)} />
        ))}
      </RadioGroup>
      <Field label="Note" {...(code === CANCEL_CODE_NEEDING_NOTE ? { isRequired: true } : { isOptional: true })}>
        {({ inputId }) => <Textarea id={inputId} rows={2} value={note} onChange={(event) => setNote(event.target.value)} />}
      </Field>
      <p className={styles.panelBody}>{wasTold ? 'The candidate and the panel are told. The candidate is never given the reason.' : 'Nothing was booked, so only the recruiter is told.'}</p>
      <div className={styles.panelActions}>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Keep it
        </Button>
        <Button
          variant="danger"
          size="sm"
          isPending={isPending}
          disabled={code === null || needsNote}
          onClick={async () => {
            if (!code) return;
            setIsPending(true);
            const isDone = await actions.cancel(interview, code, note.trim() === '' ? null : note.trim());
            setIsPending(false);
            if (isDone) onDone();
          }}
        >
          Cancel the interview
        </Button>
      </div>
    </div>
  );
}

/** F10.10–F10.12. A panelist records "completed" only; the recruiter all three. */
export function OutcomePanel({ interview, actions, onDone, onCancel }: { readonly interview: InterviewView; readonly actions: Actions; readonly onDone: () => void; readonly onCancel: () => void }): ReactNode {
  const [isPanelistNoShow, setIsPanelistNoShow] = useState(false);
  const [absent, setAbsent] = useState<ReadonlySet<string>>(new Set());
  const record = async (outcome: 'completed' | 'candidate_no_show' | 'panelist_no_show'): Promise<void> => {
    await actions.recordOutcome(interview, { outcome, absentUserIds: outcome === 'panelist_no_show' ? [...absent] : [] });
    onDone();
  };

  if (isPanelistNoShow) {
    return (
      <div className={styles.panel}>
        <p className={styles.panelTitle}>Who did not come?</p>
        <div className={styles.choices}>
          {interview.panel.map((member) => (
            <Checkbox
              key={member.userId}
              label={member.fullName}
              checked={absent.has(member.userId)}
              onChange={(isOn) =>
                setAbsent((current) => {
                  const next = new Set(current);
                  if (isOn) next.add(member.userId);
                  else next.delete(member.userId);
                  return next;
                })
              }
            />
          ))}
        </div>
        <p className={styles.panelBody}>HR is told too. It shows on the department scoreboard.</p>
        <div className={styles.panelActions}>
          <Button variant="ghost" size="sm" onClick={() => setIsPanelistNoShow(false)}>
            Back
          </Button>
          <Button variant="danger" size="sm" disabled={absent.size === 0} onClick={() => void record('panelist_no_show')}>
            Record the no-show
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <p className={styles.panelTitle}>How did it go?</p>
      <div className={styles.choices}>
        <Button variant="solid" onClick={() => void record('completed')}>
          It happened — completed
        </Button>
        {interview.can.manage && (
          <>
            <Button variant="outline" onClick={() => void record('candidate_no_show')}>
              The candidate did not come
            </Button>
            <Button variant="outline" onClick={() => setIsPanelistNoShow(true)}>
              A panelist did not come
            </Button>
          </>
        )}
      </div>
      <p className={styles.panelBody}>Completed starts the scorecard deadline ({interview.status === 'completed' ? 'running' : 'a day by default'}).</p>
      <div className={styles.panelActions}>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Not yet
        </Button>
      </div>
    </div>
  );
}

/** A panelist's "I can't make it" — the recruiter is told; nothing changes for the candidate (S19 Q10). */
export function CantMakeItPanel({ interview, actions, onDone, onCancel }: { readonly interview: InterviewView; readonly actions: Actions; readonly onDone: () => void; readonly onCancel: () => void }): ReactNode {
  const [note, setNote] = useState('');
  return (
    <div className={styles.panel}>
      <p className={styles.panelTitle}>Can't make it?</p>
      <p className={styles.panelBody}>The recruiter is told, and swaps someone in or moves the interview. The candidate hears nothing until then.</p>
      <Field label="Note" isOptional>
        {({ inputId }) => <Textarea id={inputId} rows={2} value={note} placeholder="Travelling that week." onChange={(event) => setNote(event.target.value)} />}
      </Field>
      <div className={styles.panelActions}>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="solid"
          size="sm"
          onClick={async () => {
            await actions.cantMakeIt(interview, note.trim() === '' ? null : note.trim());
            onDone();
          }}
        >
          Tell the recruiter
        </Button>
      </div>
    </div>
  );
}
